import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, MapPin, Package } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Textarea } from 'components/ui/textarea';
import { StatusBadge } from 'components/StatusBadge';
import { StatusTimeline } from 'components/StatusTimeline';
import api from 'lib/api';
import { formatDateTime } from 'lib/format';
import { useLanguage } from 'lib/i18n';
import { LanguageSwitcher } from 'components/LanguageSwitcher';

export default function TrackingPage() {
  const { t } = useLanguage();
  const { trackingToken } = useParams();
  const [tracking, setTracking] = useState(null);
  const [error, setError] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueReason, setIssueReason] = useState('not_received');
  const [issueDescription, setIssueDescription] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    const load = () => api.get(`/missions/track/${trackingToken}`)
      .then((response) => { if (active) { setTracking(response.data); setError(false); } })
      .catch(() => { if (active) setError(true); });
    load();
    const timer = setInterval(load, 30000);
    return () => { active = false; clearInterval(timer); };
  }, [trackingToken]);

  const confirmReceipt = async () => {
    setBusy(true);
    try {
      await api.post(`/missions/track/${trackingToken}/confirm`);
      setTracking((current) => ({ ...current, recipient_confirmation: { confirmed_at: new Date().toISOString() } }));
    } catch (err) {
      window.alert(t('tracking.confirmUnavailable'));
    } finally { setBusy(false); }
  };

  const reportIssue = async () => {
    if (issueDescription.trim().length < 5) return;
    setBusy(true);
    try {
      await api.post(`/missions/track/${trackingToken}/issue`, { reason: issueReason, description: issueDescription.trim() });
      setIssueOpen(false);
      setTracking((current) => ({ ...current, status: 'dispute' }));
    } catch (err) {
      window.alert(t('tracking.reportUnavailable'));
    } finally { setBusy(false); }
  };

  if (error) {
    return <main className="min-h-screen bg-muted/30 px-4 py-16"><div className="mx-auto max-w-lg rounded-lg border bg-background p-6 text-center"><AlertTriangle className="mx-auto mb-3 text-destructive" /><h1 className="text-xl font-bold">{t('tracking.notFound')}</h1><p className="mt-2 text-sm text-muted-foreground">{t('tracking.checkEmail')}</p></div></main>;
  }
  if (!tracking) return <main className="min-h-screen bg-muted/30 px-4 py-16 text-center text-muted-foreground">{t('tracking.loading')}</main>;

  const delivery = tracking.delivery || {};
  const location = tracking.current_location || {};
  return (
    <main className="min-h-screen bg-muted/30 px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-center justify-between"><Link to="/" className="text-sm font-semibold text-primary">ColiBuddy</Link><LanguageSwitcher /></div>
        <section className="mt-4 rounded-lg border bg-background p-5 shadow-sm sm:p-7">
          <div className="flex items-start justify-between gap-3">
            <div><p className="text-sm text-muted-foreground">{t('tracking.title')}</p><h1 className="mt-1 text-2xl font-bold">{delivery.title}</h1></div>
            <StatusBadge status={tracking.status} />
          </div>
          <div className="mt-5 grid gap-3 border-y py-4 text-sm sm:grid-cols-2">
            <div><p className="text-muted-foreground">{t('tracking.departure')}</p><p className="font-medium">{delivery.departure_city}</p></div>
            <div><p className="text-muted-foreground">{t('tracking.arrival')}</p><p className="font-medium">{delivery.arrival_city}</p></div>
          </div>
          {(delivery.pickup_start_time || delivery.delivery_start_time) && <div className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
            {delivery.pickup_start_time && <div><p className="text-muted-foreground">{t('tracking.pickup')}</p><p className="font-medium">{formatDateTime(delivery.pickup_start_time)} - {formatDateTime(delivery.pickup_end_time)}</p></div>}
            {delivery.delivery_start_time && <div><p className="text-muted-foreground">{t('tracking.handoff')}</p><p className="font-medium">{formatDateTime(delivery.delivery_start_time)} - {formatDateTime(delivery.delivery_end_time)}</p></div>}
          </div>}
          {location.updated_at && <div className="mt-5 flex items-center gap-2 rounded-md bg-secondary p-3 text-sm"><MapPin size={17} className="text-primary" /><span>{t('tracking.locationUpdated')} {formatDateTime(location.updated_at)}</span></div>}
          <div className="mt-7"><h2 className="mb-4 flex items-center gap-2 font-semibold"><Package size={18} /> {t('tracking.progress')}</h2><StatusTimeline status={tracking.status} history={tracking.status_history || []} /></div>
          <div className="mt-7 border-t pt-5">
            {tracking.recipient_confirmation ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 size={18} /> {t('tracking.confirmed')}</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button onClick={confirmReceipt} disabled={busy || !['in_transit', 'arrived_relay', 'delivered', 'return_requested'].includes(tracking.status)}><CheckCircle2 size={15} className="mr-1" /> {t('tracking.confirm')}</Button>
                <Button variant="outline" onClick={() => setIssueOpen(true)} disabled={busy}><AlertTriangle size={15} className="mr-1" /> {t('tracking.report')}</Button>
              </div>
            )}
          </div>
        </section>
      </div>
      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('tracking.problemTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={issueReason} onChange={(event) => setIssueReason(event.target.value)}>
              <option value="not_received">{t('tracking.parcelNotReceived')}</option><option value="damaged">{t('tracking.damaged')}</option><option value="late">{t('tracking.late')}</option><option value="other">{t('tracking.other')}</option>
            </select>
            <Textarea value={issueDescription} onChange={(event) => setIssueDescription(event.target.value)} placeholder={t('tracking.describe')} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIssueOpen(false)}>{t('tracking.cancel')}</Button><Button onClick={reportIssue} disabled={busy || issueDescription.trim().length < 5}>{t('tracking.send')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}